<script lang="ts" setup>
import type { MobileMenuItem } from '~/types/SiteSettings';

const mobileMenu = ref<MobileMenuItem[]>([]);

const siteStore = useSiteStore();
mobileMenu.value = await siteStore.getMobileMenu();

const { isShowAboveHeaderBlock, mobileMenuExpandedIds } = storeToRefs(siteStore);
const route = useRoute();

watch(
  () => route.name,
  () => {
    if (siteStore.isMobileMenuOpen) siteStore.toggleMobileMenu();
  },
);

function isExpanded(item: MobileMenuItem) {
  return mobileMenuExpandedIds.value.includes(item.id);
}

function toggleItem(item: MobileMenuItem) {
  if (isExpanded(item)) {
    mobileMenuExpandedIds.value = mobileMenuExpandedIds.value.filter((i) => i !== item.id);
  } else {
    mobileMenuExpandedIds.value.push(item.id);
  }
}

function checkForIsExpandable(item: MobileMenuItem) {
  return item.children && item.children.length;
}
</script>

<template>
  <nav
    :class="{ 'with-top-bar': isShowAboveHeaderBlock }"
    class="mobile-menu"
  >
    <template
      v-for="item in mobileMenu"
      :key="item.id"
    >
      <button
        v-if="checkForIsExpandable(item)"
        class="mobile-menu__item"
        name="expandable mobile menu item"
        @click="toggleItem(item)"
      >
        <div class="flex items-center gap-3">
          <div
            v-if="item.data?.icon"
            class="size-6"
            v-html="item.data?.icon"
          />

          {{ item.name }}
        </div>

        <SvgoBack
          :class="{ 'rotate-90': isExpanded(item) }"
          class="text-xs transition-transform -rotate-90"
          filled
        />
      </button>

      <AppLink
        v-else
        :to="makeUrlRelative(item.value)"
        class="mobile-menu__item"
        @click="siteStore.toggleMobileMenu"
      >
        <div class="flex items-center gap-3">
          <img
            v-if="item.image_url"
            :alt="item.name"
            :src="item.image_url"
            class="size-6"
          />
          <div
            v-else-if="item.data?.icon"
            class="size-6"
            v-html="item.data?.icon"
          />

          {{ item.name }}
        </div>
      </AppLink>

      <template v-if="isExpanded(item)">
        <AppLink
          v-for="child in item.children"
          :key="child.id"
          :to="makeUrlRelative(child.value)"
          class="mobile-menu__item mobile-menu__item--child"
          @click="siteStore.toggleMobileMenu"
        >
          <div class="flex items-center gap-2">
            <img
              v-if="child.image_url"
              :alt="child.name"
              :src="child.image_url"
              class="size-6"
            />
            <svg
              v-else-if="child.data?.icon"
              class="w-7 h-6"
              v-html="child.data?.icon"
            />

            {{ child.name }}
          </div>
        </AppLink>
      </template>
    </template>
  </nav>
</template>

<style lang="scss" scoped>
.mobile-menu {
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  width: 100%;
  height: calc(100dvh - 123px);
  background-color: $white;
  font-size: $text-sm;

  &.with-top-bar {
    height: calc(100dvh - 151px);
  }

  @include screen-md {
    height: calc(100dvh - 58px);

    &.with-top-bar {
      height: calc(100dvh - 86px);
    }
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid $black-10;
    font-weight: $bold;

    &--child {
      font-weight: $regular;
    }
  }
}
</style>
